/**
 * 🚨 useProblemasRibbon — Hook de configuração do Ribbon para módulo Problemas (MASP)
 *
 * Retorna RibbonConfig com grupos: Operação, Execução, Ações, Visual
 * Lógica de etapas MASP dinâmicas resolvida internamente.
 */

import { useMemo } from 'react';
import {
  MessageSquarePlus, UserCheck, ChevronRight, ChevronLeft,
  ClipboardList,
  Shield, Eye, CheckCircle2, XCircle,
  List, Grid3X3, BarChart3, RefreshCw, Download,
  FileText, FileSpreadsheet,
} from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';
import type { Issue, IssueStatus } from '../types/issue';
import {
  ISSUE_STATUS_LABELS,
  getStatusTransitions,
  getStageOrder,
} from '../types/issue';

const NOOP = () => {};

export interface ProblemasRibbonDeps {
  // Seleção
  selectedIssue: Issue | null;
  // Visualização
  viewMode: 'list' | 'kanban';
  onViewModeChange: (mode: 'list' | 'kanban') => void;
  showDashboard: boolean;
  onToggleDashboard: () => void;
  // Filtros
  hasActiveFilters: boolean;
  // Operação
  onNewIssue: () => void;
  onAssign: () => void;
  onAdvanceStage: () => void;
  onRetreatStage: () => void;
  // Execução
  onCreateLinkedAction: () => void;
  // Ações de controle
  onValidate: () => void;
  onMonitor: () => void;
  onCloseIssue: () => void;
  onCancelIssue: () => void;
  // Exportar
  onExportPDF: () => void;
  onExportExcel: () => void;
  onRefresh: () => void;
  afterGroups?: React.ReactNode;
}

export function useProblemasRibbon(deps: ProblemasRibbonDeps): RibbonConfig {
  const {
    selectedIssue,
    viewMode,
    onViewModeChange,
    showDashboard,
    onToggleDashboard,
    onNewIssue,
    onAssign,
    onAdvanceStage,
    onRetreatStage,
    onCreateLinkedAction,
    onValidate,
    onMonitor,
    onCloseIssue,
    onCancelIssue,
    onExportPDF,
    onExportExcel,
    onRefresh,
    afterGroups,
  } = deps;

  return useMemo<RibbonConfig>(() => {
    const hasSel = Boolean(selectedIssue);
    const status = selectedIssue?.status;
    const hasNoAssignee = hasSel && !selectedIssue?.assigned_to;

    // ── MASP stage logic ──
    let nextLabel = '';
    let prevLabel = '';
    let canAdvance = false;
    let canRetreat = false;

    if (status && selectedIssue) {
      const STAGE_ORDER = getStageOrder(selectedIssue.mode);
      const allowed = getStatusTransitions(selectedIssue.mode)[status] ?? [];
      const currentIdx = STAGE_ORDER.indexOf(status);
      const forwardTargets = allowed.filter((s: IssueStatus) => STAGE_ORDER.indexOf(s) > currentIdx);
      const backTargets = allowed.filter((s: IssueStatus) => STAGE_ORDER.indexOf(s) < currentIdx);
      const next = forwardTargets.length > 0 ? forwardTargets[0] : null;
      const prev = backTargets.length > 0 ? backTargets[backTargets.length - 1] : null;
      nextLabel = next ? ISSUE_STATUS_LABELS[next] : '';
      prevLabel = prev ? ISSUE_STATUS_LABELS[prev] : '';
      canAdvance = Boolean(next);
      canRetreat = Boolean(prev);
    }

    const canValidate = hasSel && (status === 'execution' || status === 'validation');
    const canMonitor = hasSel && (status === 'validation' || status === 'monitoring');
    const canClose = hasSel && status !== 'closed';

    return {
      moduleId: 'issues',
      groups: [
        // ─── Operação (LEFT) ──────────────────────
        {
          id: 'operacao',
          label: 'Operação',
          position: 'left',
          actions: [
            { id: 'new-issue', label: 'Problema', icon: MessageSquarePlus, onClick: onNewIssue },
            { id: 'assign', label: 'Assumir', icon: UserCheck, onClick: onAssign, hidden: !hasNoAssignee },
            {
              id: 'advance',
              label: canAdvance ? `Avançar: ${nextLabel}` : 'Avançar',
              icon: ChevronRight,
              onClick: onAdvanceStage,
              disabled: !canAdvance,
              variant: 'primary',
            },
            {
              id: 'retreat',
              label: canRetreat ? `Voltar: ${prevLabel}` : 'Voltar',
              icon: ChevronLeft,
              onClick: onRetreatStage,
              disabled: !canRetreat,
            },
          ],
        },
        // ─── Execução (LEFT) ──────────────────────
        {
          id: 'execucao',
          label: 'Execução',
          position: 'left',
          actions: [
            { id: 'create-action', label: 'Criar Ação', icon: ClipboardList, onClick: onCreateLinkedAction, disabled: !hasSel },
          ],
        },
        // ─── Ações (LEFT) — dropdown ──────────────
        {
          id: 'acoes',
          label: 'Ações',
          position: 'left',
          actions: [
            {
              id: 'control-actions',
              label: 'Ações',
              icon: Shield,
              onClick: NOOP,
              disabled: !hasSel,
              dropdown: [
                { id: 'validate', label: 'Validar', icon: Shield, onClick: onValidate, disabled: !canValidate },
                { id: 'monitor', label: 'Monitorar', icon: Eye, onClick: onMonitor, disabled: !canMonitor },
                { id: 'divider-1', label: '', onClick: NOOP, divider: true },
                { id: 'close', label: 'Encerrar', icon: CheckCircle2, onClick: onCloseIssue, disabled: !canClose },
                { id: 'cancel', label: 'Cancelar', icon: XCircle, onClick: onCancelIssue, disabled: !canClose },
              ],
            },
          ],
        },
        // ─── Visual (LEFT) ───────────────────────
        {
          id: 'visual',
          label: 'Visual',
          position: 'left',
          actions: [
            { id: 'view-list', label: 'Lista', icon: List, onClick: () => onViewModeChange('list'), active: viewMode === 'list' && !showDashboard },
            { id: 'view-kanban', label: 'Kanban', icon: Grid3X3, onClick: () => onViewModeChange('kanban'), active: viewMode === 'kanban' && !showDashboard },
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3, onClick: onToggleDashboard, active: showDashboard },
          ],
        },
        // ─── Exportar (LEFT) ──────────────────────
        {
          id: 'exportar',
          label: 'Exportar',
          position: 'left',
          actions: [
            {
              id: 'export-menu',
              label: 'Exportar',
              icon: Download,
              onClick: NOOP,
              dropdown: [
                { id: 'export-pdf', label: 'Exportar PDF', icon: FileText, onClick: onExportPDF },
                { id: 'export-excel', label: 'Exportar Excel', icon: FileSpreadsheet, onClick: onExportExcel },
              ],
            },
            { id: 'refresh', label: 'Atualizar', icon: RefreshCw, onClick: onRefresh },
          ],
        },
      ],
      slots: {
        afterGroups,
      },
    };
  }, [
    selectedIssue,
    viewMode,
    onViewModeChange,
    showDashboard,
    onToggleDashboard,
    onNewIssue,
    onAssign,
    onAdvanceStage,
    onRetreatStage,
    onCreateLinkedAction,
    onValidate,
    onMonitor,
    onCloseIssue,
    onCancelIssue,
    onExportPDF,
    onExportExcel,
    onRefresh,
    afterGroups,
  ]);
}
